import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'

import Network from '@/Network'
import type { DSAttendanceByCompanyResponse } from '@/Network/Services/DsAttendanceService'
import { DS_ATTENDANCE_KANBAN_PAGE_SIZE } from '@/constants/ds-attendance-sla.constants'
import type {
  DSAttendanceContainer,
  DsAttendanceSlaCounters,
  ListContainersByCompanyParams
} from '@/types/ds-attendance.types'

import { dsAttendanceKanbanColumnTotal } from '../utils/queue-kanban-columns'
import type { DsAttendanceKanbanColumn } from '../utils/queue-kanban-columns'
import type { DsAttendanceQueueBaseParams } from './useDsAttendanceQueueFilters'

/** Teto de páginas mantidas na recarga do mesmo recorte (8 × 25 = 200 cartões por coluna). */
export const DS_ATTENDANCE_KANBAN_MAX_KEPT_PAGES = 8

interface ColumnBucket {
  rows: DSAttendanceContainer[]
  /** Páginas de 25 já carregadas — o "Ver mais" pede `pages + 1`. */
  pages: number
  /** `count` do servidor para a coluna; `null` quando a 1ª página falhou (o "Ver mais" refaz). */
  count: number | null
}

/**
 * Kanban de Registro de plantão com carga POR COLUNA (spec 2026-10-01 §7.2, padrão de
 * `usePriorityKanbanColumns`, PR #2991):
 * - recorte novo (filtro, aba, agrupamento) → as colunas voltam à 1ª página, em paralelo, junto
 *   com o `sla-counters` (4–5 consultas, sem polling — G2-D13);
 * - mesmo recorte (`reloadToken`: ação, foco, "Atualizar") → cada coluna mantém as páginas;
 * - guarda de geração: resposta de recorte antigo nunca escreve estado;
 * - fora do Kanban não busca nada.
 *
 * O `baseParams` chega pronto: quem monta (o `Index.vue`) usa o `serverNow` do servidor como
 * `ctx.nowMs` (F5) — o `closedSince` do Encerrado nunca sai do relógio do cliente.
 */
export function useDsAttendanceKanbanColumns(options: {
  columns: Readonly<Ref<DsAttendanceKanbanColumn[]>>
  isActive: Readonly<Ref<boolean>>
  baseParams: Readonly<Ref<DsAttendanceQueueBaseParams | null>>
  reloadToken: Readonly<Ref<number>>
  onLoadMoreError: (error: unknown) => void
}): {
  rows: ComputedRef<DSAttendanceContainer[]>
  totals: ComputedRef<Partial<Record<string, number>>>
  hasMore: ComputedRef<Partial<Record<string, boolean>>>
  loading: Ref<Partial<Record<string, boolean>>>
  initialLoading: ComputedRef<boolean>
  failed: ComputedRef<boolean>
  serverNow: Ref<string | null>
  counters: Ref<DsAttendanceSlaCounters | null>
  loadMore: (columnKey: string) => Promise<void>
} {
  const buckets = ref<Record<string, ColumnBucket>>({})
  const counters = ref<DsAttendanceSlaCounters | null>(null)
  const serverNow = ref<string | null>(null)
  const loading = ref<Partial<Record<string, boolean>>>({})
  const loadedScope = ref<string | null>(null)
  let generation = 0

  const scopeKey = (): string | null => {
    const base = options.baseParams.value
    return base ? JSON.stringify([options.columns.value.map((column) => column.key), base]) : null
  }

  const isCurrent = computed(() => loadedScope.value !== null && loadedScope.value === scopeKey())

  async function fetchCounters(
    base: DsAttendanceQueueBaseParams
  ): Promise<DsAttendanceSlaCounters | null> {
    try {
      return await Network.dsAttendance.getSlaCounters({
        ...base,
        page: 1,
        limit: DS_ATTENDANCE_KANBAN_PAGE_SIZE
      })
    } catch {
      // Sem contador o cabeçalho cai na contagem carregada; a coluna continua de pé.
      return null
    }
  }

  async function fetchColumn(
    column: DsAttendanceKanbanColumn,
    base: DsAttendanceQueueBaseParams,
    pages: number,
    previous: ColumnBucket | undefined
  ): Promise<{ bucket: ColumnBucket; serverNow: string | null }> {
    const query = column.query(base)
    if (query === null) return { bucket: { rows: [], pages: 0, count: 0 }, serverNow: null }
    try {
      const response = await Network.dsAttendance.listByCompany({
        ...base,
        ...query,
        page: 1,
        limit: pages * DS_ATTENDANCE_KANBAN_PAGE_SIZE
      })
      return {
        bucket: { rows: response?.data ?? [], pages, count: response?.count ?? 0 },
        serverNow: response?.serverNow ?? null
      }
    } catch {
      // Refresh que falha não apaga a coluna. Sem nada antes, `count: null` liga o "Ver mais",
      // que vira nova tentativa da página 1 (`pages: 0`).
      return { bucket: previous ?? { rows: [], pages: 0, count: null }, serverNow: null }
    }
  }

  /** Páginas a pedir na recarga: as que a coluna já tinha no mesmo recorte, com teto. */
  const keptPages = (previous: ColumnBucket | undefined): number =>
    Math.min(Math.max(previous?.pages ?? 1, 1), DS_ATTENDANCE_KANBAN_MAX_KEPT_PAGES)

  /** Aplica uma recarga completa; o `serverNow` do contador vence o das colunas. */
  function commitReload(
    scope: string,
    nextCounters: DsAttendanceSlaCounters | null,
    entries: ReadonlyArray<readonly [string, { bucket: ColumnBucket; serverNow: string | null }]>
  ): void {
    const next: Record<string, ColumnBucket> = {}
    for (const [key, result] of entries) next[key] = result.bucket
    const columnServerNow = entries.find(([, result]) => result.serverNow)?.[1].serverNow ?? null
    buckets.value = next
    counters.value = nextCounters
    serverNow.value = nextCounters?.serverNow ?? columnServerNow ?? serverNow.value
    loadedScope.value = scope
  }

  async function reload(): Promise<void> {
    // Todo gatilho (inclusive sair do Kanban) invalida o que está em voo: resposta de recorte,
    // agrupamento ou "Ver mais" antigos nunca escreve estado (Review Focus 3).
    const current = ++generation
    loading.value = {}
    const base = options.baseParams.value
    const scope = scopeKey()
    if (!options.isActive.value || !base || scope === null) {
      // Fora do Kanban o recorte carregado deixa de valer: ao voltar, nada de cartões de antes
      // (recarga do zero, com skeleton) em vez de mostrar o quadro velho até a resposta.
      if (!options.isActive.value) {
        loadedScope.value = null
        buckets.value = {}
        counters.value = null
      }
      return
    }
    const previous = scope === loadedScope.value ? buckets.value : {}
    const [nextCounters, entries] = await Promise.all([
      fetchCounters(base),
      Promise.all(
        options.columns.value.map(async (column) => {
          const kept = previous[column.key]
          return [column.key, await fetchColumn(column, base, keptPages(kept), kept)] as const
        })
      )
    ])
    if (current === generation) commitReload(scope, nextCounters, entries)
  }

  /** Coluna pronta para o "Ver mais": recorte atual carregado, sem busca em voo. */
  function loadMoreTarget(columnKey: string): {
    bucket: ColumnBucket
    params: ListContainersByCompanyParams
  } | null {
    const base = options.baseParams.value
    const column = options.columns.value.find((candidate) => candidate.key === columnKey)
    const bucket = buckets.value[columnKey]
    if (!base || !column || !bucket || loading.value[columnKey] || !isCurrent.value) return null
    const query = column.query(base)
    if (query === null) return null
    const params = {
      ...base,
      ...query,
      page: bucket.pages + 1,
      limit: DS_ATTENDANCE_KANBAN_PAGE_SIZE
    }
    return { bucket, params }
  }

  const appendPage = (
    bucket: ColumnBucket,
    response: DSAttendanceByCompanyResponse | null | undefined,
    page: number
  ): ColumnBucket => {
    const seen = new Set(bucket.rows.map((row) => row.id))
    return {
      rows: [...bucket.rows, ...(response?.data ?? []).filter((row) => !seen.has(row.id))],
      pages: page,
      count: response?.count ?? bucket.count
    }
  }

  async function loadMore(columnKey: string): Promise<void> {
    const target = loadMoreTarget(columnKey)
    if (!target) return
    const current = generation
    // Além da geração, o bucket tem de ser o MESMO: uma recarga do mesmo recorte (`reloadToken`)
    // que terminou durante a busca já trocou a coluna, e anexar sobre o bucket antigo
    // ressuscitaria linhas de antes do refresh (ex.: cartão já aprovado).
    const isStale = () => current !== generation || buckets.value[columnKey] !== target.bucket
    loading.value = { ...loading.value, [columnKey]: true }
    try {
      const response = await Network.dsAttendance.listByCompany(target.params)
      if (isStale()) return
      buckets.value = {
        ...buckets.value,
        [columnKey]: appendPage(target.bucket, response, target.params.page)
      }
    } catch (error) {
      if (!isStale()) options.onLoadMoreError(error)
    } finally {
      if (current === generation) loading.value = { ...loading.value, [columnKey]: false }
    }
  }

  /**
   * União das colunas na ordem delas, sem repetir (o Kanban põe cada linha na 1ª que casa).
   * Como `totals`/`hasMore`: enquanto o recorte novo carrega, nenhum cartão do anterior.
   */
  const rows = computed<DSAttendanceContainer[]>(() => {
    if (!options.isActive.value || !isCurrent.value) return []
    const seen = new Set<number>()
    const result: DSAttendanceContainer[] = []
    for (const column of options.columns.value) {
      for (const row of buckets.value[column.key]?.rows ?? []) {
        if (seen.has(row.id)) continue
        seen.add(row.id)
        result.push(row)
      }
    }
    return result
  })

  const totals = computed<Partial<Record<string, number>>>(() => {
    const base = options.baseParams.value
    // Como o `hasMore`: nada de mostrar os totais do recorte anterior enquanto o novo carrega.
    if (!options.isActive.value || !base || !isCurrent.value) return {}
    const result: Partial<Record<string, number>> = {}
    for (const column of options.columns.value) {
      const total =
        dsAttendanceKanbanColumnTotal(column, counters.value, base) ??
        buckets.value[column.key]?.count ??
        undefined
      if (total !== undefined && total !== null) result[column.key] = total
    }
    return result
  })

  const hasMore = computed<Partial<Record<string, boolean>>>(() => {
    if (!isCurrent.value) return {}
    const result: Partial<Record<string, boolean>> = {}
    for (const [key, bucket] of Object.entries(buckets.value)) {
      // `count: null` = a 1ª página falhou: o "Ver mais" fica visível como nova tentativa.
      result[key] = bucket.count === null || bucket.rows.length < bucket.count
    }
    return result
  })

  /**
   * Kanban ativo esperando a 1ª resposta do recorte atual (skeleton do mockup "carregando").
   * Sem recorte ainda (`baseParams` null: a fila monta os filtros depois dos combos) também é
   * "carregando" — senão o quadro pinta as colunas vazias por um instante (B-07, fase 7).
   */
  const initialLoading = computed(() => options.isActive.value && !isCurrent.value)

  /** Coluna pulada pelo recorte (`query === null`): não buscou, não falhou. */
  const isSkipped = (bucket: ColumnBucket): boolean => bucket.pages === 0 && bucket.count === 0

  /**
   * Falha total (mockup "erro"): sem contador e TODA coluna buscada falhou na 1ª página. Uma
   * coluna só falhando continua no quadro com o "Ver mais" como nova tentativa.
   */
  const failed = computed(() => {
    if (!options.isActive.value || !isCurrent.value || counters.value !== null) return false
    const fetched = Object.values(buckets.value).filter((bucket) => !isSkipped(bucket))
    return fetched.length > 0 && fetched.every((bucket) => bucket.count === null)
  })

  watch(
    [options.baseParams, options.reloadToken, options.columns, options.isActive],
    () => {
      void reload()
    },
    { immediate: true }
  )

  return {
    rows,
    totals,
    hasMore,
    loading,
    initialLoading,
    failed,
    serverNow,
    counters,
    loadMore
  }
}
